// shared/crafting — secret: quality roll distribution, recipe expansion.
// Per plan §4: rollQuality MUST take a seed. No global RNG.

import type { Quality } from '../tiers/index.js';
import {
  RECIPES,
  BASE_QUALITY_WEIGHTS,
  MASTERY_QUALITY_SHIFT_PER_LEVEL,
  type CraftingRecipe,
  type StationKind,
} from './data.js';

export type { CraftingRecipe, StationKind } from './data.js';

export interface IngredientList {
  ingredients: ReadonlyArray<{ defId: string; count: number }>;
}

const RECIPES_BY_ID = new Map<string, CraftingRecipe>(RECIPES.map((r) => [r.id, r]));
const RECIPES_BY_OUTPUT = new Map<string, CraftingRecipe[]>();
for (const r of RECIPES) {
  const list = RECIPES_BY_OUTPUT.get(r.outputDefId) ?? [];
  list.push(r);
  RECIPES_BY_OUTPUT.set(r.outputDefId, list);
}

export function recipeById(id: string): CraftingRecipe | null {
  return RECIPES_BY_ID.get(id) ?? null;
}

export function recipesAtStation(kind: StationKind): ReadonlyArray<CraftingRecipe> {
  return RECIPES.filter((r) => r.station === kind);
}

export function recipesProducing(defId: string): ReadonlyArray<CraftingRecipe> {
  return RECIPES_BY_OUTPUT.get(defId) ?? [];
}

export function expand(recipe: CraftingRecipe): IngredientList {
  return { ingredients: recipe.ingredients };
}

// Mastery-shifted weights — moves probability mass from Normal toward higher qualities,
// up to a soft cap at level ~50 (where Masterpiece becomes a real possibility for great crafters).
function weightsAtMastery(masteryLevel: number): ReadonlyArray<[Quality, number]> {
  const lvl = Math.max(0, Math.min(99, masteryLevel));
  const shift = lvl * MASTERY_QUALITY_SHIFT_PER_LEVEL;
  const base = BASE_QUALITY_WEIGHTS;
  const normalIdx = base.findIndex(([q]) => q === 'Normal');
  if (normalIdx < 0) return base;
  const normalEntry = base[normalIdx];
  if (!normalEntry) return base;
  const normalWeight = normalEntry[1];
  const removed = Math.min(normalWeight, shift);
  // distribute removed mass: 50% to Good, 30% to Outstanding, 15% to Excellent, 5% to Masterpiece.
  const dist: Record<Quality, number> = {
    Normal: -removed,
    Good: removed * 0.5,
    Outstanding: removed * 0.3,
    Excellent: removed * 0.15,
    Masterpiece: removed * 0.05,
  };
  return base.map(([q, w]) => [q, Math.max(0, w + dist[q])]);
}

export function rollQuality(seed: number, recipe: CraftingRecipe, masteryLevel: number): Quality {
  const weights = weightsAtMastery(masteryLevel);
  // Reproducible draw from seed (we use a small splitmix-like step).
  let s = (seed ^ recipe.id.length * 0x9e3779b1) >>> 0;
  s = Math.imul(s ^ (s >>> 16), 0x85ebca6b) >>> 0;
  s = Math.imul(s ^ (s >>> 13), 0xc2b2ae35) >>> 0;
  s = (s ^ (s >>> 16)) >>> 0;
  let roll = (s / 4294967296) * weights.reduce((a, [, w]) => a + w, 0);
  for (const [q, w] of weights) {
    roll -= w;
    if (roll <= 0) return q;
  }
  return weights[weights.length - 1]?.[0] ?? 'Normal';
}

export function canAfford(
  recipe: CraftingRecipe,
  inventory: ReadonlyMap<string, number>
): boolean {
  for (const ing of recipe.ingredients) {
    if ((inventory.get(ing.defId) ?? 0) < ing.count) return false;
  }
  return true;
}
